import React, { memo, useId } from 'react';

/**
 * Эскиз прямоугольного треугольника по катетам a и b.
 *
 * @param {number|string} sideA      – катет a (мм)
 * @param {number|string} sideB      – катет b (мм)
 * @param {boolean}       [hasError] – true → размеры выводятся красным
 */
function TrianglePreview({ sideA, sideB, hasError = false }) {
  // приводим “сырые” значения к числам
  const a = Number(sideA) || null;
  const b = Number(sideB) || null;
  const c = a && b ? Math.hypot(a, b) : null;        // гипотенуза

  const markerId = useId();                           // уникальный id для <marker>
  const LINE = '#4487C4'; // Sky Blue
  const DIM  = hasError ? '#ff4d4f' : 'black';

  // Геометрия макета
  const P0 = { x: 110, y: 240 };   // прямой угол
  const P1 = { x: 110, y:  40 };   // верх-лево
  const P2 = { x: 410, y: 240 };   // низ-право

  const GAP_CAT = 32;  // вынос стрелок катетов
  const OFF_C   = 26;  // расстояние стрелки-размера c от гипотенузы
  const SHRINK  = 6;   // “усадка” концов стрелки-c внутрь
  const INSIDE  = 22;  // смещение подписи «Сторона c» внутрь треугольника

  // Вектор гипотенузы и нормаль к нему
  const vx = P2.x - P1.x;
  const vy = P2.y - P1.y;
  const vLen = Math.hypot(vx, vy);
  const nx =  vy / vLen;          // наружная нормаль
  const ny = -vx / vLen;

  // Концы размерной линии c
  const C1 = {
    x: P1.x + nx * OFF_C + (vx / vLen) * SHRINK,
    y: P1.y + ny * OFF_C + (vy / vLen) * SHRINK,
  };
  const C2 = {
    x: P2.x + nx * OFF_C - (vx / vLen) * SHRINK,
    y: P2.y + ny * OFF_C - (vy / vLen) * SHRINK,
  };

  // Координаты подписи “Сторона c” и цифры c
  const CMmid = { x: (P1.x + P2.x) / 2, y: (P1.y + P2.y) / 2 };
  const CMtxt = { x: CMmid.x - nx * INSIDE, y: CMmid.y - ny * INSIDE };
  const CMout = { x: (C1.x + C2.x) / 2,     y: (C1.y + C2.y) / 2 - 4 };

  // Угол наклона гипотенузы для поворота текста
  const rawDeg   = Math.atan2(vy, vx) * 180 / Math.PI;
  const slopeDeg = (rawDeg < -90 || rawDeg > 90) ? rawDeg + 180 : rawDeg;

  return (
    <svg width="100%" style={{ maxWidth: '420px', height: 'auto' }} viewBox="0 0 520 320">
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="10"
          refY="5"
          markerWidth="10"
          markerHeight="10"
          orient="auto-start-reverse"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0 0L10 5L0 10Z" fill={LINE} />
        </marker>
      </defs>

      {/* контур треугольника */}
      <polyline
        points={`${P0.x},${P0.y} ${P1.x},${P1.y} ${P2.x},${P2.y} ${P0.x},${P0.y}`}
        fill="none"
        stroke={LINE}
        strokeWidth="2"
      />

      {/* катет a */}
      <line
        x1={P0.x - GAP_CAT} y1={P1.y}
        x2={P0.x - GAP_CAT} y2={P0.y}
        stroke={LINE} strokeWidth="2"
        markerStart={`url(#${markerId})`} markerEnd={`url(#${markerId})`}
      />
      <text
        x={P0.x - GAP_CAT - 10}
        y={(P0.y + P1.y) / 2}
        transform={`rotate(-90 ${P0.x - GAP_CAT - 10} ${(P0.y + P1.y) / 2})`}
        textAnchor="middle"
        fontFamily="Commissioner"
        fontSize="16" fontWeight="600"
        fill={DIM}
      >
        {a ? `${a} мм` : '? мм'}
      </text>

      {/* катет b */}
      <line
        x1={P0.x} y1={P0.y + GAP_CAT}
        x2={P2.x} y2={P2.y + GAP_CAT}
        stroke={LINE} strokeWidth="2"
        markerStart={`url(#${markerId})`} markerEnd={`url(#${markerId})`}
      />
      <text
        x={(P0.x + P2.x) / 2}
        y={P0.y + GAP_CAT + 18}
        textAnchor="middle"
        fontFamily="Commissioner"
        fontSize="16" fontWeight="600"
        fill={DIM}
      >
        {b ? `${b} мм` : '? мм'}
      </text>

      {/* гипотенуза c */}
      <line
        x1={C1.x} y1={C1.y} x2={C2.x} y2={C2.y}
        stroke={LINE} strokeWidth="2"
        markerStart={`url(#${markerId})`} markerEnd={`url(#${markerId})`}
      />
      <text
        x={CMout.x} y={CMout.y - 2}
        transform={`rotate(${slopeDeg} ${CMout.x} ${CMout.y})`}
        textAnchor="middle"
        fontFamily="Commissioner"
        fontSize="16" fontWeight="600"
        fill={DIM}
      >
        {c ? `${c.toFixed(0)} мм` : '? мм'}
      </text>

      {/* подписи сторон */}
      <text
        x={P0.x + 10}
        y={(P0.y + P1.y + 10) / 2}
        transform={`rotate(-90 ${P0.x + 10} ${(P0.y + P1.y) / 2})`}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="Commissioner"
        fontSize="14" fontWeight="400"
        fill="black"
      >
        Сторона a
      </text>

      <text
        x={(P0.x + P2.x) / 2}
        y={P0.y - 14}
        textAnchor="middle"
        fontFamily="Commissioner"
        fontSize="14" fontWeight="400"
        fill="black"
      >
        Сторона b
      </text>

      <text
        x={CMtxt.x} y={CMtxt.y}
        transform={`rotate(${slopeDeg} ${CMtxt.x} ${CMtxt.y})`}
        textAnchor="middle"
        fontFamily="Commissioner"
        fontSize="14" fontWeight="400"
        fill="black"
      >
        Сторона c
      </text>
    </svg>
  );
}

export default memo(TrianglePreview);
